/**
 * AnimatedCtaPreview
 *
 * Live preview for the new animated CTA templates (v2):
 *   - frosted_glass — frosted overlay + shimmer-sweep button
 *   - urgency_strip — full-width banner + pulsing red dot, slide-in
 *   - split_badge   — two-tone badge with toggling lock icon
 *   - neon_glow     — pulsing neon border (heartbeat)
 *   - minimal_float — bouncing-lock pill button, no headline
 *
 * IMPORTANT: All templates honor `colors.btn_bg` and `colors.btn_text`. The
 * configurable accent (banner_bg, dot, glow, badge_bg, badge_text, headline)
 * falls back to opinionated defaults.
 *
 * The matching production CSS for embed.js lives in
 * `backend/system/routes.py::injectCtaStyles` — keep the keyframes in sync.
 */
import { useEffect } from "react";

// ============== Animation CSS (injected once) ==============
// Scoped under `.vd-animated-cta` so it can't leak into the rest of the admin UI.
const ANIM_CSS = `
.vd-animated-cta {
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-sizing: border-box;
}
.vd-animated-cta *, .vd-animated-cta *::before, .vd-animated-cta *::after { box-sizing: border-box; }

/* ---------- Frosted Glass ---------- */
.vd-tmpl-frosted_glass {
  position: relative;
  border-radius: 14px;
  padding: 20px 18px;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04)),
    var(--vd-frame, linear-gradient(160deg, #93C5FD 0%, #4338CA 100%));
  overflow: hidden;
}
.vd-tmpl-frosted_glass .vd-fg-card {
  background: var(--vd-overlay, rgba(255,255,255,0.65));
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255,255,255,0.6);
  border-radius: 12px;
  padding: 18px 16px;
  text-align: center;
}
.vd-tmpl-frosted_glass .vd-fg-headline {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--vd-headline, #0F172A);
  margin: 0 0 14px;
}
.vd-tmpl-frosted_glass .vd-fg-btn {
  position: relative;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%;
  border: none; cursor: pointer;
  background: var(--vd-btn-bg);
  color: var(--vd-btn-text);
  border-radius: 999px;
  padding: 12px 22px;
  font-size: 15px; font-weight: 700;
  overflow: hidden;
  isolation: isolate;
}
.vd-tmpl-frosted_glass .vd-fg-btn::before {
  content: ""; position: absolute; top: 0; left: -75%; width: 50%; height: 100%;
  background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%);
  transform: skewX(-22deg);
  animation: vd-shimmer 2.4s linear infinite;
  pointer-events: none;
}
@keyframes vd-shimmer {
  0%   { left: -75%; }
  100% { left: 125%; }
}

/* ---------- Urgency Strip ---------- */
.vd-tmpl-urgency_strip {
  width: 100%;
  background: var(--vd-banner-bg, #0F172A);
  color: var(--vd-headline, #FFFFFF);
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  animation: vd-slide-up 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.vd-tmpl-urgency_strip .vd-us-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--vd-dot, #EF4444);
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6);
  animation: vd-pulse-dot 1.4s ease-out infinite;
  flex-shrink: 0;
}
.vd-tmpl-urgency_strip .vd-us-text {
  flex: 1; min-width: 0;
  font-size: 13px; font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.vd-tmpl-urgency_strip .vd-us-btn {
  border: none; cursor: pointer;
  background: var(--vd-btn-bg);
  color: var(--vd-btn-text);
  font-weight: 700; font-size: 13px;
  padding: 8px 16px; border-radius: 6px;
  flex-shrink: 0;
  transition: filter 0.15s ease;
}
.vd-tmpl-urgency_strip .vd-us-btn:hover { filter: brightness(1.1); }
@keyframes vd-pulse-dot {
  0%   { box-shadow: 0 0 0 0   rgba(239, 68, 68, 0.6); }
  70%  { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);  }
  100% { box-shadow: 0 0 0 0   rgba(239, 68, 68, 0);  }
}
@keyframes vd-slide-up {
  0%   { opacity: 0; transform: translateY(18px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* Mobile: stack the urgency strip */
@media (max-width: 480px) {
  .vd-tmpl-urgency_strip { flex-wrap: wrap; }
  .vd-tmpl-urgency_strip .vd-us-text { white-space: normal; }
  .vd-tmpl-urgency_strip .vd-us-btn { width: 100%; margin-top: 6px; }
}

/* ---------- Split Badge ---------- */
.vd-tmpl-split_badge {
  display: inline-flex; align-items: stretch;
  width: 100%; min-height: 52px;
  border-radius: 10px; overflow: hidden;
  box-shadow: 0 4px 14px rgba(0,0,0,0.10);
}
.vd-tmpl-split_badge .vd-sb-left {
  flex: 0 0 auto;
  background: var(--vd-badge-bg, #0F172A);
  color: var(--vd-badge-text, #FACC15);
  padding: 10px 14px;
  display: flex; align-items: center; gap: 8px;
  font-weight: 800; font-size: 12px; letter-spacing: 0.08em;
}
.vd-tmpl-split_badge .vd-sb-lock {
  width: 22px; height: 22px;
  position: relative; display: inline-block;
  animation: vd-lock-toggle 2.6s ease-in-out infinite;
}
.vd-tmpl-split_badge .vd-sb-lock svg { width: 100%; height: 100%; }
.vd-tmpl-split_badge .vd-sb-btn {
  flex: 1; min-width: 0;
  border: none; cursor: pointer;
  background: var(--vd-btn-bg);
  color: var(--vd-btn-text);
  font-weight: 700; font-size: 14px;
  padding: 0 16px;
  display: flex; align-items: center; justify-content: center;
  transition: filter 0.15s ease;
}
.vd-tmpl-split_badge .vd-sb-btn:hover { filter: brightness(1.08); }
@keyframes vd-lock-toggle {
  0%, 35%   { transform: rotate(0deg); }
  45%, 80%  { transform: rotate(-12deg) translateY(-1px); }
  100%      { transform: rotate(0deg); }
}
@media (max-width: 420px) {
  .vd-tmpl-split_badge { flex-direction: column; }
  .vd-tmpl-split_badge .vd-sb-left { justify-content: center; }
  .vd-tmpl-split_badge .vd-sb-btn { padding: 12px 16px; }
}

/* ---------- Neon Glow ---------- */
.vd-tmpl-neon_glow {
  background: #0B1120;
  padding: 24px 18px;
  border-radius: 12px;
  display: flex; justify-content: center;
}
.vd-tmpl-neon_glow .vd-ng-btn {
  position: relative;
  background: var(--vd-btn-bg, #0B1120);
  color: var(--vd-btn-text, #A5F3FC);
  border: 2px solid var(--vd-glow, #22D3EE);
  border-radius: 999px;
  padding: 14px 28px;
  font-weight: 700; font-size: 15px; letter-spacing: 0.06em;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  animation: vd-neon-pulse 1.6s ease-in-out infinite;
  transition: transform 0.15s ease;
}
.vd-tmpl-neon_glow .vd-ng-btn:hover { transform: translateY(-2px); }
@keyframes vd-neon-pulse {
  0%, 100% {
    box-shadow:
      0 0 0   var(--vd-glow, #22D3EE),
      0 0 8px var(--vd-glow, #22D3EE),
      0 0 18px var(--vd-glow, #22D3EE);
  }
  50% {
    box-shadow:
      0 0 0   var(--vd-glow, #22D3EE),
      0 0 14px var(--vd-glow, #22D3EE),
      0 0 32px var(--vd-glow, #22D3EE);
  }
}

/* ---------- Minimal Float ---------- */
.vd-tmpl-minimal_float {
  display: flex; justify-content: center;
  padding: 8px 4px;
}
.vd-tmpl-minimal_float .vd-mf-btn {
  position: relative;
  display: inline-flex; align-items: center; gap: 10px;
  background: var(--vd-btn-bg, #111827);
  color: var(--vd-btn-text, #FFFFFF);
  border: none; cursor: pointer;
  border-radius: 999px;
  padding: 14px 26px;
  font-weight: 700; font-size: 15px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.18);
  animation: vd-float-bounce 2.4s ease-in-out infinite;
  transition: transform 0.2s ease;
}
.vd-tmpl-minimal_float .vd-mf-lock {
  width: 18px; height: 18px;
  display: inline-flex; align-items: center;
  transition: transform 0.3s ease;
}
.vd-tmpl-minimal_float .vd-mf-btn:hover .vd-mf-lock { transform: rotate(-12deg); }
@keyframes vd-float-bounce {
  0%, 100% { transform: translateY(0);    }
  50%      { transform: translateY(-6px); }
}

/* Respect reduced-motion preferences across all templates */
@media (prefers-reduced-motion: reduce) {
  .vd-tmpl-frosted_glass .vd-fg-btn::before,
  .vd-tmpl-urgency_strip,
  .vd-tmpl-urgency_strip .vd-us-dot,
  .vd-tmpl-split_badge .vd-sb-lock,
  .vd-tmpl-neon_glow .vd-ng-btn,
  .vd-tmpl-minimal_float .vd-mf-btn {
    animation: none !important;
  }
}

/* Lock SVG sizing inside animated templates */
.vd-animated-cta .vd-fg-btn svg,
.vd-animated-cta .vd-ng-btn svg {
  width: 18px; height: 18px;
  flex-shrink: 0;
}
.vd-animated-cta .vd-mf-lock svg,
.vd-animated-cta .vd-sb-lock svg {
  width: 100%; height: 100%;
  display: block;
}

/* ---------- iter123: Blurred Price Reveal ---------- */
.vd-tmpl-blurred_price_reveal {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: var(--vd-card-bg, #FFFFFF);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15,23,42,0.05);
  text-align: left;
}
.vd-tmpl-blurred_price_reveal .vd-bpr-msrp {
  margin: 0;
  color: var(--vd-muted, #6B7280);
  font-size: 13px;
}
.vd-tmpl-blurred_price_reveal .vd-bpr-msrp strong {
  color: var(--vd-headline, #0F172A);
  font-weight: 600;
}
.vd-tmpl-blurred_price_reveal .vd-bpr-price {
  margin: 0;
  color: var(--vd-headline, #0F172A);
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}
.vd-tmpl-blurred_price_reveal .vd-bpr-blur {
  display: inline-block;
  color: var(--vd-accent, #166CA2);
  filter: blur(5px);
  user-select: none;
  letter-spacing: 0.5px;
  transition: filter .25s ease;
}
.vd-tmpl-blurred_price_reveal:hover .vd-bpr-blur { filter: blur(3.5px); }
.vd-tmpl-blurred_price_reveal .vd-bpr-btn {
  width: 100%;
  margin-top: 4px;
  padding: 12px 18px;
  border: none;
  border-radius: 999px;
  background: var(--vd-btn-bg, #166CA2);
  color: var(--vd-btn-text, #FFFFFF);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.vd-tmpl-blurred_price_reveal .vd-bpr-btn svg { width: 16px; height: 16px; flex-shrink: 0; }

/* ---------- iter123: Live Activity ---------- */
.vd-tmpl-live_activity {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: transparent;
  text-align: center;
}
.vd-tmpl-live_activity .vd-la-line {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: var(--vd-muted, #475569);
  font-weight: 500;
}
.vd-tmpl-live_activity .vd-la-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--vd-dot, #DC2626);
  box-shadow: 0 0 0 0 rgba(220,38,38,0.55);
  animation: vd-la-pulse 1.5s ease-out infinite;
}
@keyframes vd-la-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(220,38,38,0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(220,38,38,0); }
  100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
}
.vd-tmpl-live_activity .vd-la-btn {
  width: 100%;
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: var(--vd-btn-bg, #0F172A);
  color: var(--vd-btn-text, #FFFFFF);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.vd-tmpl-live_activity .vd-la-btn svg { width: 16px; height: 16px; flex-shrink: 0; }

/* ---------- iter123: Savings First ---------- */
.vd-tmpl-savings_first {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  background: var(--vd-card-bg, #ECFDF5);
  border: 1px solid var(--vd-accent, #10B981);
  border-radius: 12px;
  text-align: left;
}
.vd-tmpl-savings_first .vd-sf-headline {
  margin: 0;
  font-size: 15px;
  color: var(--vd-headline, #064E3B);
  line-height: 1.3;
}
.vd-tmpl-savings_first .vd-sf-amount {
  color: var(--vd-accent, #10B981);
  font-weight: 800;
  font-size: 19px;
}
.vd-tmpl-savings_first .vd-sf-btn {
  width: 100%;
  margin-top: 6px;
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: var(--vd-btn-bg, #10B981);
  color: var(--vd-btn-text, #FFFFFF);
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.vd-tmpl-savings_first .vd-sf-btn svg { width: 16px; height: 16px; flex-shrink: 0; }

@media (prefers-reduced-motion: reduce) {
  .vd-tmpl-live_activity .vd-la-dot { animation: none !important; }
}
`;

const STYLE_ID = "vd-animated-cta-styles-v2";
function ensureStylesInjected() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = ANIM_CSS;
  document.head.appendChild(style);
}

const LockSvg = ({ stroke = "currentColor" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 1 1 8 0v3" />
  </svg>
);

export const AnimatedCtaPreview = ({ templateId, colors = {}, buttonText = "Unlock Best Price" }) => {
  useEffect(() => { ensureStylesInjected(); }, []);

  // CSS custom properties feed the animations + base colors
  const buildFrameValue = (raw) => {
    if (!raw) return null;
    const v = String(raw).trim();
    if (!v) return null;
    if (v.indexOf("gradient") !== -1) return v;
    return `linear-gradient(160deg, ${v} 0%, ${v} 100%)`;
  };
  const cssVars = {
    "--vd-btn-bg": colors.btn_bg || "#111827",
    "--vd-btn-text": colors.btn_text || "#FFFFFF",
    "--vd-headline": colors.headline || (templateId === "urgency_strip" ? "#FFFFFF" : "#0F172A"),
    "--vd-overlay": colors.overlay_bg || "rgba(255,255,255,0.65)",
    "--vd-banner-bg": colors.banner_bg || "#0F172A",
    "--vd-dot": colors.dot || "#EF4444",
    "--vd-badge-bg": colors.badge_bg || "#0F172A",
    "--vd-badge-text": colors.badge_text || "#FACC15",
    "--vd-glow": colors.glow || "#22D3EE",
    // iter123 — additive tokens consumed by blurred_price_reveal / live_activity / savings_first
    "--vd-card-bg": colors.card_bg || (templateId === "savings_first" ? "#ECFDF5" : "#FFFFFF"),
    "--vd-muted": colors.muted || (templateId === "live_activity" ? "#475569" : "#6B7280"),
    "--vd-accent": colors.accent || (templateId === "savings_first" ? "#10B981" : "#166CA2"),
  };
  const frameVal = buildFrameValue(colors.frame_bg);
  if (frameVal) cssVars["--vd-frame"] = frameVal;

  switch (templateId) {
    case "frosted_glass":
      return (
        <div className="vd-animated-cta vd-tmpl-frosted_glass" style={cssVars} data-testid="cta-preview-frosted_glass">
          <div className="vd-fg-card">
            <p className="vd-fg-headline">EXCLUSIVE PRICE</p>
            <button type="button" className="vd-fg-btn" data-testid="cta-preview-button">
              <LockSvg stroke="currentColor" />
              <span>{buttonText}</span>
            </button>
          </div>
        </div>
      );

    case "urgency_strip":
      return (
        <div className="vd-animated-cta vd-tmpl-urgency_strip" style={cssVars} data-testid="cta-preview-urgency_strip">
          <span className="vd-us-dot" aria-hidden="true" />
          <span className="vd-us-text">LIMITED TIME — UNLOCK PRICING NOW</span>
          <button type="button" className="vd-us-btn" data-testid="cta-preview-button">
            {buttonText}
          </button>
        </div>
      );

    case "split_badge":
      return (
        <div className="vd-animated-cta vd-tmpl-split_badge" style={cssVars} data-testid="cta-preview-split_badge">
          <div className="vd-sb-left">
            <span className="vd-sb-lock"><LockSvg stroke="currentColor" /></span>
            <span>EXCLUSIVE PRICE</span>
          </div>
          <button type="button" className="vd-sb-btn" data-testid="cta-preview-button">
            {buttonText}
          </button>
        </div>
      );

    case "neon_glow":
      return (
        <div className="vd-animated-cta vd-tmpl-neon_glow" style={cssVars} data-testid="cta-preview-neon_glow">
          <button type="button" className="vd-ng-btn" data-testid="cta-preview-button">
            <LockSvg stroke="currentColor" />
            <span>{buttonText}</span>
          </button>
        </div>
      );

    case "minimal_float":
      return (
        <div className="vd-animated-cta vd-tmpl-minimal_float" style={cssVars} data-testid="cta-preview-minimal_float">
          <button type="button" className="vd-mf-btn" data-testid="cta-preview-button">
            <span className="vd-mf-lock"><LockSvg stroke="currentColor" /></span>
            <span>{buttonText}</span>
          </button>
        </div>
      );

    // iter123 — new templates ----------------------------------------
    case "blurred_price_reveal":
      return (
        <div className="vd-animated-cta vd-tmpl-blurred_price_reveal" style={cssVars} data-testid="cta-preview-blurred_price_reveal">
          <p className="vd-bpr-msrp">MSRP <strong>$48,990</strong></p>
          <p className="vd-bpr-price">
            Your Price:{" "}
            <span className="vd-bpr-blur" aria-hidden="true">$XX,XXX</span>
          </p>
          <button type="button" className="vd-bpr-btn" data-testid="cta-preview-button">
            <LockSvg stroke="currentColor" />
            <span>{buttonText}</span>
          </button>
        </div>
      );

    case "live_activity":
      return (
        <div className="vd-animated-cta vd-tmpl-live_activity" style={cssVars} data-testid="cta-preview-live_activity">
          <span className="vd-la-line">
            <span className="vd-la-dot" aria-hidden="true" />
            <span className="vd-la-text">5 shoppers viewing this · 12 prices unlocked this week</span>
          </span>
          <button type="button" className="vd-la-btn" data-testid="cta-preview-button">
            <LockSvg stroke="currentColor" />
            <span>{buttonText}</span>
          </button>
        </div>
      );

    case "savings_first":
      return (
        <div className="vd-animated-cta vd-tmpl-savings_first" style={cssVars} data-testid="cta-preview-savings_first">
          <p className="vd-sf-headline">
            You could save <span className="vd-sf-amount">$3,200</span> below MSRP
          </p>
          <button type="button" className="vd-sf-btn" data-testid="cta-preview-button">
            <LockSvg stroke="currentColor" />
            <span>{buttonText}</span>
          </button>
        </div>
      );

    default:
      return null;
  }
};

export default AnimatedCtaPreview;
